import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import AdminShop from '../components/AdminShop';
import SecurityPanel from '../components/SecurityPanel';
import {
  ArrowLeft, Shield, Users, DollarSign, TrendingUp, Pause, Play,
  RefreshCw, ShoppingBag, Gavel, BarChart3, Activity, Target, Sun, Moon, Grid3x3, Trash2, Bot, Gift, Clock, X, Flag, Check, Search, Copy, Wallet, Sparkles, MessageCircle
} from 'lucide-react';

function AdminPanel() {
  const navigate = useNavigate();
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
  const [secret, setSecret] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [escrows, setEscrows] = useState([]);
  const [buybackHistory, setBuybackHistory] = useState(null);
  const [occupiedSlots, setOccupiedSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [adminRooms, setAdminRooms] = useState([]);
  const [adminAuctions, setAdminAuctions] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomFilter, setRoomFilter] = useState('all');
  const [reports, setReports] = useState([]);
  const [reportsFilter, setReportsFilter] = useState('open');
  const [settings, setSettings] = useState(null);
  const [ghostStats, setGhostStats] = useState(null);
  const [platformWallet, setPlatformWallet] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [hostApps, setHostApps] = useState([]);
  const [hostAppsFilter, setHostAppsFilter] = useState('pending');
  const [hostAppsLoading, setHostAppsLoading] = useState(false);
  const [userSearchQ, setUserSearchQ] = useState('');
  const [userSearchResults, setUserSearchResults] = useState(null); // null = idle, [] = no matches, [...] = results
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedApps, setSelectedApps] = useState({}); // { [app_id]: true }
  const [bulkLoading, setBulkLoading] = useState(false);
  const [autoReward, setAutoReward] = useState({ enabled: false, amount_xrp: 5 });
  const [autoRewardLoading, setAutoRewardLoading] = useState(false);
  const [showBuybackDialog, setShowBuybackDialog] = useState(false);
  const [showNftDialog, setShowNftDialog] = useState(false);
  const [buybackAmount, setBuybackAmount] = useState('');
  const [buybackWallet, setBuybackWallet] = useState('');
  const [nftAmount, setNftAmount] = useState('');
  const [nftId, setNftId] = useState('');
  const [nftWallet, setNftWallet] = useState('');
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawDest, setWithdrawDest] = useState('');
  const [withdrawTag, setWithdrawTag] = useState('');
  const [withdrawNote, setWithdrawNote] = useState('');
  const [treasuryHistory, setTreasuryHistory] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphanCreditFor, setOrphanCreditFor] = useState(null); // holds an orphan row
  const [orphanCreditUser, setOrphanCreditUser] = useState('');
  const [orphanCreditNote, setOrphanCreditNote] = useState('');
  const [orphanCreditLoading, setOrphanCreditLoading] = useState(false);
  const [showReconcileDialog, setShowReconcileDialog] = useState(false);
  const [reconcilePreview, setReconcilePreview] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (adminToken) { fetchAll(); fetchAutoReward(); fetchOrphans(); }
  }, [adminToken]);

  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });

  const handleLogin = async () => {
    try {
      const response = await axios.post(`${API}/admin/login`, { secret });
      setAdminToken(response.data.token);
      localStorage.setItem('admin_token', response.data.token);
      toast.success('Admin access granted');
    } catch {
      toast.error('Invalid admin secret');
    }
  };

  const fetchAll = async () => {
    try {
      const [dashRes, treasRes, escrowRes, histRes, analyticsRes, settingsRes, ghostRes, pwRes, twRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers: getHeaders() }),
        axios.get(`${API}/admin/treasury`, { headers: getHeaders() }),
        axios.get(`${API}/admin/escrows`, { headers: getHeaders() }),
        axios.get(`${API}/admin/buyback-history`, { headers: getHeaders() }),
        axios.get(`${API}/admin/analytics`, { headers: getHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/settings`, { headers: getHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/ghost-stats`, { headers: getHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/xrpl/platform-wallet`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/treasury/history`, { headers: getHeaders() }).catch(() => ({ data: { withdrawals: [] } })),
      ]);
      setDashboard(dashRes.data);
      setTreasury(treasRes.data);
      setEscrows(escrowRes.data.escrows);
      setBuybackHistory(histRes.data);
      setAnalytics(analyticsRes.data);
      setSettings(settingsRes.data);
      setGhostStats(ghostRes.data);
      setPlatformWallet(pwRes.data);
      setTreasuryHistory(twRes.data.withdrawals || []);
    } catch {
      toast.error('Failed to fetch admin data');
      setAdminToken(null);
      localStorage.removeItem('admin_token');
    }
  };

  const toggleGhostPlayer = async () => {
    const next = !settings?.ghost_player_enabled;
    try {
      await axios.post(`${API}/admin/settings`, { ghost_player_enabled: next }, { headers: getHeaders() });
      setSettings(s => ({ ...(s || {}), ghost_player_enabled: next }));
      toast.success(next ? 'Ghost Player enabled — auto-fills cheap rooms' : 'Ghost Player disabled');
      // Refresh stats too
      try {
        const r = await axios.get(`${API}/admin/ghost-stats`, { headers: getHeaders() });
        setGhostStats(r.data);
      } catch { /* ignore */ }
    } catch { toast.error('Failed to toggle'); }
  };

  const [groupChatIdDraft, setGroupChatIdDraft] = useState('');
  const [savingGroupChat, setSavingGroupChat] = useState(false);
  useEffect(() => {
    setGroupChatIdDraft(settings?.group_chat_id || '');
  }, [settings?.group_chat_id]);

  const saveGroupChatId = async () => {
    setSavingGroupChat(true);
    try {
      const clean = String(groupChatIdDraft || '').trim();
      await axios.post(
        `${API}/admin/settings`,
        { group_chat_id: clean },
        { headers: getHeaders() },
      );
      setSettings(s => ({ ...(s || {}), group_chat_id: clean }));
      toast.success(clean ? 'Group chat updated — Fireball shout-outs will now fire' : 'Group chat cleared');
    } catch {
      toast.error('Failed to save group chat id');
    } finally {
      setSavingGroupChat(false);
    }
  };

  const [testingGroupChat, setTestingGroupChat] = useState(false);
  const sendGroupTestMessage = async () => {
    setTestingGroupChat(true);
    try {
      await axios.post(`${API}/admin/settings/test-group-message`, {}, { headers: getHeaders() });
      toast.success('Test message sent — check the group chat');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test message failed');
    } finally {
      setTestingGroupChat(false);
    }
  };

  const [fuzzyStatus, setFuzzyStatus] = useState(null);
  const [fuzzyLoading, setFuzzyLoading] = useState(false);
  const [fuzzyRoomName, setFuzzyRoomName] = useState('');
  const fetchFuzzyStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/fuzzy/status`, { headers: getHeaders() });
      setFuzzyStatus(res.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (adminToken) fetchFuzzyStatus(); }, [adminToken]);

  const setupFuzzyTrustline = async () => {
    setFuzzyLoading(true);
    try {
      const res = await axios.post(`${API}/admin/fuzzy/trustline`, {}, { headers: getHeaders() });
      toast.success(res.data.already_exists ? 'Trustline already active' : 'FUZZY trustline created on platform wallet');
      fetchFuzzyStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Trustline setup failed');
    } finally {
      setFuzzyLoading(false);
    }
  };

  const [fuzzyAnnouncing, setFuzzyAnnouncing] = useState(false);
  const announceHoneyPot = async () => {
    if (!window.confirm("Post the Honey Pot launch announcement to the community group now?")) return;
    setFuzzyAnnouncing(true);
    try {
      await axios.post(`${API}/admin/fuzzy/announce`, {}, { headers: getHeaders() });
      toast.success('Honey Pot launch post sent to the group! 🍯');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Announcement failed');
    } finally {
      setFuzzyAnnouncing(false);
    }
  };

  const [cleaningTestData, setCleaningTestData] = useState(false);
  const cleanupTestData = async () => {
    if (!window.confirm('Remove ALL test rooms (TEST_/PYTEST_) and ghost players? This cannot be undone.')) return;
    setCleaningTestData(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup-test-data`, {}, { headers: getHeaders() });
      const d = res.data.deleted || {};
      toast.success(`Cleaned: ${d.test_rooms || 0} rooms, ${d.ghost_users || 0} ghost users, ${d.ghost_purchases || 0} ghost buys`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaningTestData(false);
    }
  };

  const toggleHostedFees = async () => {
    const next = !settings?.hosted_fee_waived;
    try {
      await axios.post(`${API}/admin/settings`, { hosted_fee_waived: next }, { headers: getHeaders() });
      setSettings(s => ({ ...(s || {}), hosted_fee_waived: next }));
      toast.success(next ? 'Platform fees WAIVED on hosted prize rooms' : 'Platform fees re-enabled on hosted prize rooms');
    } catch {
      toast.error('Failed to update fee setting');
    }
  };

  const toggleOpenHosting = async () => {
    const next = !settings?.open_hosting;
    try {
      await axios.post(`${API}/admin/settings`, { open_hosting: next }, { headers: getHeaders() });
      setSettings(s => ({ ...(s || {}), open_hosting: next }));
      toast.success(next ? 'Hosting is now OPEN to everyone' : 'Hosting is back to approval-only');
    } catch {
      toast.error('Failed to update hosting setting');
    }
  };

  const createFuzzyRoom = async () => {
    const name = fuzzyRoomName.trim();
    if (!name) { toast.error('Enter a room name'); return; }
    setFuzzyLoading(true);
    try {
      await axios.post(`${API}/admin/fuzzy-rooms`, { room_name: name }, { headers: getHeaders() });
      toast.success(`FUZZY room "${name}" created`);
      setFuzzyRoomName('');
      fetchFuzzyStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Room creation failed');
    } finally {
      setFuzzyLoading(false);
    }
  };

  const handlePauseGames = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/pause-games`, {}, { headers: getHeaders() });
      toast.success(`${res.data.paused_count} games paused`);
      fetchAll();
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleResumeGames = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/resume-games`, {}, { headers: getHeaders() });
      toast.success(`${res.data.resumed_count} games resumed`);
      fetchAll();
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleBuyback = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/trigger-buyback`, { amount: parseFloat(buybackAmount), wallet_address: buybackWallet, token_currency: 'GHOST' }, { headers: getHeaders() });
      toast.success('Buyback triggered!');
      setShowBuybackDialog(false);
      setBuybackAmount('');
      setBuybackWallet('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleNftPurchase = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/trigger-nft-purchase`, { amount: parseFloat(nftAmount), nft_id: nftId, wallet_address: nftWallet }, { headers: getHeaders() });
      toast.success('NFT purchase triggered!');
      setShowNftDialog(false);
      setNftAmount('');
      setNftId('');
      setNftWallet('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const fetchOrphans = async () => {
    setOrphansLoading(true);
    try {
      const r = await axios.get(`${API}/admin/orphan-deposits?limit=25`, { headers: getHeaders() });
      setOrphans(r.data.orphans || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch orphan deposits');
    } finally { setOrphansLoading(false); }
  };

  const submitOrphanCredit = async () => {
    if (!orphanCreditFor) return;
    const uname = (orphanCreditUser || '').trim().replace(/^@/, '');
    if (!uname) { toast.error('Enter a username'); return; }
    if (!window.confirm(`Credit ${orphanCreditFor.amount_xrp} XRP to @${uname}?\n\nThe on-chain XRP is already in the platform wallet — this only updates the DB.`)) return;
    setOrphanCreditLoading(true);
    try {
      const body = { username: uname, tx_hash: orphanCreditFor.tx_hash };
      if (orphanCreditNote.trim()) body.note = orphanCreditNote.trim();
      const r = await axios.post(`${API}/admin/orphan-deposits/credit`, body, { headers: getHeaders() });
      toast.success(`Credited ${r.data.amount_xrp} XRP to @${r.data.credited_to.username} · new balance ${r.data.new_balance.toFixed(2)} XRP`);
      setOrphanCreditFor(null);
      setOrphanCreditUser('');
      setOrphanCreditNote('');
      fetchOrphans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Credit failed');
    } finally { setOrphanCreditLoading(false); }
  };



  const openReconcile = async () => {
    setReconcilePreview(null);
    setShowReconcileDialog(true);
    setReconcileLoading(true);
    try {
      const r = await axios.post(`${API}/admin/treasury/reconcile`, { dry_run: true }, { headers: getHeaders() });
      setReconcilePreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
      setShowReconcileDialog(false);
    } finally { setReconcileLoading(false); }
  };

  const commitReconcile = async () => {
    if (!reconcilePreview) return;
    setReconcileLoading(true);
    try {
      const r = await axios.post(`${API}/admin/treasury/reconcile`, { dry_run: false }, { headers: getHeaders() });
      toast.success(`Treasury reconciled → ${r.data.computed_total_fees.toFixed(4)} XRP (Δ ${r.data.delta > 0 ? '+' : ''}${r.data.delta.toFixed(4)})`);
      setShowReconcileDialog(false);
      setReconcilePreview(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconcile failed');
    } finally { setReconcileLoading(false); }
  };


  const handleTreasuryWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    const dest = (withdrawDest || '').trim();
    const currentFees = Number(treasury?.treasury?.total_fees || 0);
    if (!(amt > 0)) { toast.error('Amount must be > 0'); return; }
    if (amt > currentFees + 1e-6) { toast.error(`Only ${currentFees.toFixed(4)} XRP available`); return; }
    if (!dest.startsWith('r') || dest.length < 25 || dest.length > 35) {
      toast.error('Destination must be a valid XRPL classic address'); return;
    }
    if (!window.confirm(`Send ${amt} XRP from platform treasury to ${dest}?\n\nThis is a real on-chain XRPL transaction.`)) return;
    setLoading(true);
    try {
      const body = { amount_xrp: amt, destination: dest };
      if (withdrawTag.trim()) body.destination_tag = parseInt(withdrawTag.trim(), 10);
      if (withdrawNote.trim()) body.note = withdrawNote.trim();
      const res = await axios.post(`${API}/admin/treasury/withdraw`, body, { headers: getHeaders() });
      toast.success(`Sent ${res.data.amount_xrp} XRP · tx ${res.data.tx_hash?.slice(0, 12) || 'pending'}...`);
      setShowWithdrawDialog(false);
      setWithdrawAmount(''); setWithdrawDest(''); setWithdrawTag(''); setWithdrawNote('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Withdrawal failed');
    } finally { setLoading(false); }
  };

  const resolveEscrow = async (escrowId, resolution) => {
    try {
      await axios.post(`${API}/admin/escrow/${escrowId}/resolve`, { resolution, note: `Admin resolved: ${resolution}` }, { headers: getHeaders() });
      toast.success(`Escrow ${resolution}d`);
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const fetchOccupiedSlots = async () => {
    setSlotsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/slots/occupied`, { headers: getHeaders() });
      setOccupiedSlots(res.data.occupied_slots || []);
    } catch { toast.error('Failed to load slots'); }
    finally { setSlotsLoading(false); }
  };

  const handleClearSlot = async (purchaseId, refund = true) => {
    if (!window.confirm(refund ? 'Clear slot AND refund the user?' : 'Clear slot WITHOUT refund?')) return;
    try {
      const res = await axios.post(`${API}/admin/slots/clear`,
        { purchase_id: purchaseId, refund, note: 'Cleared via admin panel' },
        { headers: getHeaders() }
      );
      toast.success(res.data.refunded ? `Cleared & refunded ${res.data.refunded_amount} XRP` : 'Slot cleared');
      fetchOccupiedSlots();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCleanupStale = async () => {
    if (!window.confirm('Bulk-cancel all slot purchases with deleted owners or stale game sessions? (No refund — owners do not exist)')) return;
    try {
      const res = await axios.post(`${API}/admin/slots/cleanup-stale`, {}, { headers: getHeaders() });
      toast.success(`Cancelled ${res.data.total_cancelled} stale slots (orphan: ${res.data.orphaned_owner_cancelled}, stale: ${res.data.stale_session_cancelled})`);
      fetchOccupiedSlots();
    } catch { toast.error('Cleanup failed'); }
  };

  const fetchAdminRoomsAndAuctions = async () => {
    setRoomsLoading(true);
    try {
      const [roomsRes, auctionsRes] = await Promise.all([
        axios.get(`${API}/admin/rooms`, { headers: getHeaders() }),
        axios.get(`${API}/admin/auctions`, { headers: getHeaders() }),
      ]);
      setAdminRooms(roomsRes.data.rooms || []);
      setAdminAuctions(auctionsRes.data.auctions || []);
    } catch { toast.error('Failed to load rooms'); }
    finally { setRoomsLoading(false); }
  };

  const handleCancelRoom = async (gameId, roomName) => {
    if (!window.confirm(`Cancel "${roomName}"? All active slot owners will be auto-refunded.`)) return;
    try {
      const res = await axios.post(`${API}/admin/rooms/${gameId}/cancel`,
        { note: 'Cancelled via admin panel' }, { headers: getHeaders() });
      toast.success(`Cancelled. Refunded ${res.data.refunded_count} buyers (${res.data.refunded_total} XRP)`);
      fetchAdminRoomsAndAuctions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleReactivateRoom = async (gameId, roomName) => {
    if (!window.confirm(`Re-enable "${roomName}"? A fresh draw session will be created so users can buy slots again. Past refunds are NOT undone.`)) return;
    try {
      await axios.post(`${API}/admin/rooms/${gameId}/reactivate`, {}, { headers: getHeaders() });
      toast.success(`"${roomName}" re-enabled with fresh session`);
      fetchAdminRoomsAndAuctions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reactivate'); }
  };

  const handleCancelAuction = async (auctionId, roomName) => {
    if (!window.confirm(`Cancel auction "${roomName}"? Current high bidder will be refunded.`)) return;
    try {
      const res = await axios.post(`${API}/admin/auctions/${auctionId}/cancel`,
        { note: 'Cancelled via admin panel' }, { headers: getHeaders() });
      toast.success(res.data.refunded_amount > 0
        ? `Cancelled. Refunded ${res.data.refunded_amount} XRP to high bidder`
        : 'Cancelled (no bids to refund)');
      fetchAdminRoomsAndAuctions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleExpireIdle = async () => {
    if (!window.confirm('Auto-expire all hosted rooms older than 7 days with 0 player slots sold?')) return;
    try {
      const res = await axios.post(`${API}/admin/rooms/expire-idle`,
        { days: 7 }, { headers: getHeaders() });
      toast.success(`Expired ${res.data.cancelled} idle rooms (checked ${res.data.checked})`);
      fetchAdminRoomsAndAuctions();
    } catch { toast.error('Failed'); }
  };

  const fetchReports = async (status = 'open') => {
    try {
      const res = await axios.get(`${API}/admin/reports?status=${status}`, { headers: getHeaders() });
      setReports(res.data.reports || []);
    } catch { toast.error('Failed to load reports'); }
  };

  const fetchUsers = async (q = '') => {
    setUsersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users?q=${encodeURIComponent(q)}&limit=100`, { headers: getHeaders() });
      setUsers(res.data.users || []);
      setUsersTotal(res.data.total || 0);
    } catch { toast.error('Failed to load users'); }
    finally { setUsersLoading(false); }
  };

  const fetchHostApps = async (status = 'pending') => {
    setHostAppsLoading(true);
    setHostAppsFilter(status);
    setSelectedApps({}); // Clear selection on filter change
    try {
      const res = await axios.get(`${API}/admin/host-applications?status=${status}`, { headers: getHeaders() });
      setHostApps(res.data.applications || []);
    } catch { toast.error('Failed to load host applications'); }
    finally { setHostAppsLoading(false); }
  };

  const fetchAutoReward = async () => {
    try {
      const r = await axios.get(`${API}/admin/auto-reward/settings`, { headers: getHeaders() });
      setAutoReward(r.data.auction_champion || { enabled: false, amount_xrp: 5 });
    } catch { /* silent — feature is optional */ }
  };

  const saveAutoReward = async (enabled, amount) => {
    setAutoRewardLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/auto-reward/settings`,
        { badge_id: 'auction_champion', enabled, amount_xrp: amount },
        { headers: getHeaders() },
      );
      setAutoReward(r.data.auction_champion);
      toast.success(
        enabled
          ? `Auto-reward ON — new Auction Champions get ${amount} XRP automatically`
          : 'Auto-reward OFF'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update auto-reward');
    } finally {
      setAutoRewardLoading(false);
    }
  };

  const toggleAppSelected = (appId) => {
    setSelectedApps((prev) => {
      const next = { ...prev };
      if (next[appId]) delete next[appId]; else next[appId] = true;
      return next;
    });
  };

  const toggleAllSelected = () => {
    const pending = hostApps.filter(a => a.status === 'pending');
    if (pending.length === 0) return;
    const allSelected = pending.every(a => selectedApps[a.application_id]);
    if (allSelected) {
      setSelectedApps({});
    } else {
      const next = {};
      pending.forEach(a => { next[a.application_id] = true; });
      setSelectedApps(next);
    }
  };

  const handleBulkAction = async (action) => {
    const ids = Object.keys(selectedApps);
    if (ids.length === 0) {
      toast.info('Select at least one application');
      return;
    }
    const verb = action === 'approve' ? 'approve' : 'deny';
    if (!window.confirm(`${verb === 'approve' ? 'Approve' : 'Deny'} ${ids.length} application${ids.length > 1 ? 's' : ''}?`)) {
      return;
    }
    let reason = 'Bulk action';
    if (action === 'deny') {
      const r = window.prompt('Denial reason shown to applicants (optional):', 'Did not meet host criteria');
      if (r === null) return; // cancelled
      if (r.trim()) reason = r.trim();
    }
    setBulkLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/host-applications/bulk-action`,
        { application_ids: ids, action, reason },
        { headers: getHeaders() }
      );
      const { processed = [], skipped = [] } = res.data;
      toast.success(`${verb === 'approve' ? 'Approved' : 'Denied'} ${processed.length} application${processed.length !== 1 ? 's' : ''}${skipped.length ? ` — ${skipped.length} skipped (already decided)` : ''}`);
      setSelectedApps({});
      fetchHostApps(hostAppsFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleApproveHost = async (applicationId) => {
    try {
      await axios.post(`${API}/admin/host-applications/${applicationId}/approve`, {}, { headers: getHeaders() });
      toast.success('Host approved — they can now list games');
      fetchHostApps(hostAppsFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleDenyHost = async (applicationId) => {
    const reason = window.prompt('Denial reason (shown to applicant):', 'Did not meet host criteria');
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/host-applications/${applicationId}/deny`,
        { reason: reason.slice(0, 300) }, { headers: getHeaders() });
      toast.success('Application denied');
      fetchHostApps(hostAppsFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deny');
    }
  };

  const handleRevokeHost = async (userId, username) => {
    if (!window.confirm(`Revoke host privilege from @${username}? Their existing listings stay live; they just can't create new ones.`)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/revoke-host`, {}, { headers: getHeaders() });
      toast.success(`@${username} can no longer host`);
      fetchHostApps(hostAppsFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const handleGrantHost = async () => {
    const input = window.prompt(
      'Grant host privilege — paste a @username OR an XRPL address (r...):',
      ''
    );
    if (!input) return;
    const trimmed = input.trim().replace(/^@/, '');
    const payload = trimmed.startsWith('r') && trimmed.length >= 25
      ? { xrpl_address: trimmed }
      : { username: trimmed };
    try {
      const r = await axios.post(`${API}/admin/users/grant-host`, payload, { headers: getHeaders() });
      if (r.data.status === 'already_host') {
        toast.info(`@${r.data.username} is already an approved host`);
      } else {
        toast.success(`@${r.data.username} can now list games`);
      }
      fetchHostApps(hostAppsFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant host');
    }
  };

  const handleUserSearch = async () => {
    const q = userSearchQ.trim();
    if (q.length < 2) {
      toast.info('Type at least 2 characters');
      return;
    }
    setUserSearchLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/search`, {
        headers: getHeaders(),
        params: { q },
      });
      setUserSearchResults(r.data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Search failed');
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const grantHostToUser = async (userId, username) => {
    try {
      const r = await axios.post(`${API}/admin/users/grant-host`, { user_id: userId }, { headers: getHeaders() });
      if (r.data.status === 'already_host') {
        toast.info(`@${username} is already an approved host`);
      } else {
        toast.success(`@${username} can now list games`);
      }
      // Refresh both the search results and the applications list
      handleUserSearch();
      fetchHostApps(hostAppsFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant host');
    }
  };

  const revokeHostForUser = async (userId, username) => {
    if (!window.confirm(`Revoke host privilege from @${username}?`)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/revoke-host`, {}, { headers: getHeaders() });
      toast.success(`@${username} can no longer host`);
      handleUserSearch();
      fetchHostApps(hostAppsFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const rewardUser = async (userId, username, suggested = '5') => {
    const input = window.prompt(
      `Reward @${username} with XRP (max 100 per call):\nAmount in XRP:`,
      suggested,
    );
    if (input === null) return;
    const amount = parseFloat(input);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Enter a positive number');
      return;
    }
    const reason = window.prompt(
      'Reason (shown in the Telegram DM the user receives):',
      'Auction Champion — thank you for playing',
    );
    if (reason === null) return;
    try {
      const r = await axios.post(
        `${API}/admin/users/${userId}/reward`,
        { amount, reason: reason || 'Personal reward from admin' },
        { headers: getHeaders() },
      );
      toast.success(`Sent ${r.data.amount} XRP to @${username} — new balance ${r.data.new_balance.toFixed(2)} XRP`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reward failed');
    }
  };

  const copyToClipboard = async (value, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(label);
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleResolveReport = async (reportId, resolution) => {
    try {
      await axios.post(`${API}/admin/reports/${reportId}/resolve`,
        { resolution, note: `Admin marked: ${resolution}` }, { headers: getHeaders() });
      toast.success(`Report ${resolution}`);
      fetchReports(reportsFilter);
    } catch { toast.error('Failed'); }
  };

  if (!adminToken) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="bg-slate-800/80 border-slate-700 w-full max-w-sm">
          <div className="p-6 space-y-4">
            <div className="text-center">
              <Shield className="w-10 h-10 text-orange-400 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-white">Ghost Mode Admin</h2>
              <p className="text-xs text-slate-500 mt-1">Enter admin secret to access</p>
            </div>
            <Input data-testid="admin-secret-input" type="password" placeholder="Admin Secret" value={secret}
              onChange={(e) => setSecret(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} className="bg-slate-900 border-slate-700 text-white" />
            <Button data-testid="admin-login-btn" onClick={handleLogin} className="w-full bg-orange-500 hover:bg-orange-600">Access Admin Panel</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-slate-400 p-1"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-base font-bold text-white flex items-center gap-2"><Shield className="w-4 h-4 text-orange-400" /> Ghost Mode</h1>
              <p className="text-[11px] text-slate-500">Admin Dashboard</p>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={fetchAll} className="text-slate-400"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </header>

      {/* Stats Grid */}
      {dashboard && (
        <div className="px-4 pt-4 grid grid-cols-3 gap-2">
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-2.5 text-center">
            <Users className="w-3.5 h-3.5 text-indigo-400 mx-auto mb-0.5" />
            <p className="text-base font-bold text-white">{dashboard.stats.total_users}</p>
            <p className="text-[9px] text-slate-500">Users</p>
          </div>
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-2.5 text-center">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400 mx-auto mb-0.5" />
            <p className="text-base font-bold text-emerald-400">{dashboard.stats.treasury_balance?.toFixed(2)}</p>
            <p className="text-[9px] text-slate-500">Treasury XRP</p>
          </div>
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-2.5 text-center">
            <Target className="w-3.5 h-3.5 text-orange-400 mx-auto mb-0.5" />
            <p className="text-base font-bold text-orange-400">{dashboard.stats.total_slots_sold || 0}</p>
            <p className="text-[9px] text-slate-500">Slots Sold</p>
          </div>
        </div>
      )}

      {/* Tabs: Analytics | Controls | Escrows */}
      <div className="px-4 pt-4">
        <Tabs defaultValue="analytics" className="w-full">
          <TabsList data-testid="admin-tabs" className="w-full flex-wrap bg-slate-800/60 border border-slate-700/50 rounded-xl h-auto p-1 gap-1">
            <TabsTrigger data-testid="admin-tab-analytics" value="analytics" className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300 data-[state=active]:shadow-none text-slate-400">
              <BarChart3 className="w-3 h-3 mr-1" /> Analytics
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-controls" value="controls" className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-300 data-[state=active]:shadow-none text-slate-400">
              <Activity className="w-3 h-3 mr-1" /> Controls
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-escrows" value="escrows" className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300 data-[state=active]:shadow-none text-slate-400">
              <Gavel className="w-3 h-3 mr-1" /> Escrows
            </TabsTrigger>
            <TabsTrigger
              data-testid="admin-tab-slots"
              value="slots"
              onClick={fetchOccupiedSlots}
              className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-red-500/20 data-[state=active]:text-red-300 data-[state=active]:shadow-none text-slate-400"
            >
              <Grid3x3 className="w-3 h-3 mr-1" /> Slots
            </TabsTrigger>
            <TabsTrigger
              data-testid="admin-tab-rooms"
              value="rooms"
              onClick={fetchAdminRoomsAndAuctions}
              className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300 data-[state=active]:shadow-none text-slate-400"
            >
              <Gift className="w-3 h-3 mr-1" /> Rooms
            </TabsTrigger>
            <TabsTrigger
              data-testid="admin-tab-reports"
              value="reports"
              onClick={() => fetchReports(reportsFilter)}
              className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-300 data-[state=active]:shadow-none text-slate-400"
            >
              <Flag className="w-3 h-3 mr-1" /> Reports
            </TabsTrigger>
            <TabsTrigger
              data-testid="admin-tab-users"
              value="users"
              onClick={() => fetchUsers(userSearch)}
              className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-300 data-[state=active]:shadow-none text-slate-400"
            >
              <Users className="w-3 h-3 mr-1" /> Users
            </TabsTrigger>
            <TabsTrigger
              data-testid="admin-tab-host-apps"
              value="host-apps"
              onClick={() => fetchHostApps('pending')}
              className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300 data-[state=active]:shadow-none text-slate-400"
            >
              <Sparkles className="w-3 h-3 mr-1" /> Hosts
            </TabsTrigger>
            <TabsTrigger
              data-testid="admin-tab-shop"
              value="shop"
              className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-300 data-[state=active]:shadow-none text-slate-400"
            >
              <Gift className="w-3 h-3 mr-1" /> Shop
            </TabsTrigger>
            <TabsTrigger
              data-testid="admin-tab-security"
              value="security"
              className="flex-1 min-w-[31%] text-[11px] py-2 rounded-lg data-[state=active]:bg-red-500/20 data-[state=active]:text-red-300 data-[state=active]:shadow-none text-slate-400"
            >
              <Shield className="w-3 h-3 mr-1" /> Security
            </TabsTrigger>
          </TabsList>

          {/* Security Tab */}
          <TabsContent value="security" data-testid="tab-security" className="mt-3 space-y-3">
            <SecurityPanel headers={getHeaders()} />
          </TabsContent>

          {/* Shop Tab */}
          <TabsContent value="shop" data-testid="tab-shop" className="mt-3 space-y-3">
            <AdminShop headers={getHeaders()} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" data-testid="tab-analytics" className="mt-3 space-y-4">            {analytics ? (
              <>
                {/* Revenue Chart */}
                <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-white flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5 text-emerald-400" /> Revenue (14d)</p>
                    <span className="text-xs font-bold text-emerald-400">{analytics.revenue.total_14d} XRP</span>
                  </div>
                  <MiniBarChart data={analytics.revenue.daily} valueKey="revenue" color="bg-emerald-500" />
                </div>

                {/* Signups Chart */}
                <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-white flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-indigo-400" /> Signups (14d)</p>
                    <span className="text-xs font-bold text-indigo-400">{analytics.signups.total_14d} users</span>
                  </div>
                  <MiniBarChart data={analytics.signups.daily} valueKey="signups" color="bg-indigo-500" />
                </div>

                {/* Purchases Chart */}
                <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-white flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-orange-400" /> Slot Purchases (14d)</p>
                    <span className="text-xs font-bold text-orange-400">{analytics.purchases.total_14d} slots</span>
                  </div>
                  <MiniBarChart data={analytics.purchases.daily} valueKey="purchases" color="bg-orange-500" />
                </div>

                {/* Win Distribution */}
                <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-4">
                  <p className="text-xs font-bold text-white mb-3">Win Distribution</p>
                  <div className="flex gap-3">
                    <div className="flex-1 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center">
                      <Sun className="w-4 h-4 text-amber-400 mx-auto mb-1" />
                      <p className="text-lg font-bold text-amber-300">{analytics.win_distribution.midday}</p>
                      <p className="text-[9px] text-slate-500">Midday Wins</p>
                    </div>
                    <div className="flex-1 bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-3 text-center">
                      <Moon className="w-4 h-4 text-indigo-400 mx-auto mb-1" />
                      <p className="text-lg font-bold text-indigo-300">{analytics.win_distribution.evening}</p>
                      <p className="text-[9px] text-slate-500">Evening Wins</p>
                    </div>
                  </div>
                </div>

                {/* Game Performance */}
                {analytics.game_performance?.length > 0 && (
                  <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-4">
                    <p className="text-xs font-bold text-white mb-3">Top Rooms by Slots Sold</p>
                    <div className="space-y-2">
                      {analytics.game_performance.map((g, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-900/40 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-xs font-medium text-white">{g.room_name}</p>
                            <p className="text-[9px] text-slate-500">{g.prize_type} &middot; {g.entry_price} XRP entry</p>
                          </div>
                          <span className="text-xs font-bold text-cyan-400">{g.slots_sold} slots</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-sm text-slate-500">Loading analytics...</div>
            )}
          </TabsContent>

          {/* Controls Tab */}
          <TabsContent value="controls" data-testid="tab-controls" className="mt-3 space-y-4">
            {/* Platform Wallet */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5 text-indigo-400" /> Platform Wallet
              </p>
              <div data-testid="platform-wallet-card" className="bg-slate-800/40 border border-indigo-500/20 rounded-xl p-3">
                {platformWallet?.address ? (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] uppercase tracking-wider text-slate-500">Network</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 font-semibold uppercase">
                        {platformWallet.network || 'xrpl'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code data-testid="platform-wallet-address" className="text-[11px] text-white font-mono break-all flex-1 bg-slate-900/60 rounded px-2 py-1.5">
                        {platformWallet.address}
                      </code>
                      <button
                        data-testid="copy-platform-wallet"
                        onClick={() => copyToClipboard(platformWallet.address, 'Platform address copied')}
                        className="text-slate-400 hover:text-emerald-400 p-1.5 rounded transition"
                        aria-label="Copy platform address"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1.5">
                      Users send deposits to this address. Don't share publicly outside the app.
                    </p>
                  </>
                ) : (
                  <p className="text-[11px] text-slate-500">Platform wallet not ready yet.</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-white">Game Controls</p>
              <div className="flex gap-2">
                <Button data-testid="pause-games-btn" size="sm" onClick={handlePauseGames} disabled={loading} className="flex-1 bg-red-600/80 hover:bg-red-700 text-xs h-9">
                  <Pause className="w-3.5 h-3.5 mr-1" /> Pause All
                </Button>
                <Button data-testid="resume-games-btn" size="sm" onClick={handleResumeGames} disabled={loading} className="flex-1 bg-emerald-600/80 hover:bg-emerald-700 text-xs h-9">
                  <Play className="w-3.5 h-3.5 mr-1" /> Resume All
                </Button>
              </div>
            </div>
            {/* Open Hosting Toggle */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-white">Hosting Access</p>
              <button
                data-testid="open-hosting-toggle"
                type="button"
                onClick={toggleOpenHosting}
                className={`w-full flex items-center justify-between gap-3 rounded-xl p-3 border transition-colors ${
                  settings?.open_hosting
                    ? 'bg-purple-500/10 border-purple-500/30'
                    : 'bg-slate-800/50 border-slate-700/50'
                }`}
              >
                <div className="text-left">
                  <p className="text-xs font-bold text-white">
                    {settings?.open_hosting ? '🌐 Hosting OPEN to everyone' : 'Hosting is approval-only'}
                  </p>
                  <p className="text-[10px] text-slate-500 leading-snug">
                    When open: any logged-in player can tap Host and list NFT/physical prize rooms directly — no application needed. Flip back anytime to require admin approval again.
                  </p>
                </div>
                <span className={`flex-shrink-0 w-10 h-5 rounded-full p-0.5 transition-colors ${settings?.open_hosting ? 'bg-purple-500' : 'bg-slate-600'}`}>
                  <span className={`block w-4 h-4 rounded-full bg-white transition-transform ${settings?.open_hosting ? 'translate-x-5' : 'translate-x-0'}`} />
                </span>
              </button>
            </div>
            {/* Ghost Player Toggle */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-white">Prize Room Fees</p>
              <button
                data-testid="hosted-fee-toggle"
                type="button"
                onClick={toggleHostedFees}
                className={`w-full flex items-center justify-between gap-3 rounded-xl p-3 border transition-colors ${
                  settings?.hosted_fee_waived
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-slate-800/50 border-slate-700/50'
                }`}
              >
                <div className="text-left">
                  <p className="text-xs font-bold text-white">
                    {settings?.hosted_fee_waived ? '🎉 Buyer platform fee WAIVED (NFT & physical rooms)' : 'Buyer platform fee active (+5% per slot)'}
                  </p>
                  <p className="text-[10px] text-slate-500 leading-snug">
                    When waived: buyers pay entry + host earnings only (no platform markup). The standard 5.89% platform cut from the pool still applies. XRP pool room loyalty fees unaffected.
                  </p>
                </div>
                <span className={`flex-shrink-0 w-10 h-5 rounded-full p-0.5 transition-colors ${settings?.hosted_fee_waived ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                  <span className={`block w-4 h-4 rounded-full bg-white transition-transform ${settings?.hosted_fee_waived ? 'translate-x-5' : 'translate-x-0'}`} />
                </span>
              </button>
            </div>
            {/* Ghost Player Toggle */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-white">Ghost Player</p>
              <button
                data-testid="ghost-player-toggle"
                type="button"
                onClick={toggleGhostPlayer}
                className={`w-full flex items-center justify-between gap-3 rounded-xl p-3 border transition-colors ${
                  settings?.ghost_player_enabled
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-slate-800/50 border-slate-700/50'
                }`}
              >
                <div className="flex items-start gap-2 text-left">
                  <Bot className={`w-4 h-4 mt-0.5 ${settings?.ghost_player_enabled ? 'text-emerald-400' : 'text-slate-500'}`} />
                  <div>
                    <p className="text-xs font-bold text-white">
                      {settings?.ghost_player_enabled ? 'Enabled' : 'Disabled'}
                    </p>
                    <p className="text-[10px] text-slate-500 leading-snug">
                      Auto-fills 1–2 random slots in rooms ≤ 25 XRP every 4 hours to bootstrap activity. Slots are clearly labelled "Ghost".
                    </p>
                  </div>
                </div>
                <span
                  className={`flex-shrink-0 w-10 h-5 rounded-full p-0.5 transition-colors ${
                    settings?.ghost_player_enabled ? 'bg-emerald-500' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                      settings?.ghost_player_enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </span>
              </button>
              {/* Ghost Stats Widget */}
              {ghostStats && ghostStats.ghost_user_exists && (
                <div data-testid="ghost-stats-widget" className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-bold text-white uppercase tracking-wide">Ghost Activity</p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                      ghostStats.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-400'
                    }`}>{ghostStats.enabled ? 'LIVE' : 'PAUSED'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <GhostStat label="Slots bought" value={ghostStats.totals.slots_bought} accent="text-orange-300" />
                    <GhostStat label="Active now" value={ghostStats.totals.active_slots} accent="text-indigo-300" />
                    <GhostStat label="Wins" value={ghostStats.totals.wins} accent="text-emerald-300" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <GhostStat label="Win rate" value={`${ghostStats.totals.win_rate_pct}%`} accent="text-amber-300" />
                    <GhostStat label="Won XRP" value={ghostStats.totals.win_amount} accent="text-emerald-300" />
                    <GhostStat label="Fees added" value={ghostStats.totals.fees_contributed} accent="text-purple-300" />
                  </div>
                  {ghostStats.by_room.length > 0 && (
                    <div className="space-y-1 pt-1 border-t border-slate-700/40">
                      <p className="text-[9px] text-slate-500 uppercase tracking-wide">By Room</p>
                      {ghostStats.by_room.slice(0, 5).map((r) => (
                        <div
                          key={r.game_id}
                          data-testid={`ghost-room-${r.game_id.slice(0, 8)}`}
                          className="flex items-center justify-between text-[10px]"
                        >
                          <span className="text-white font-medium truncate">{r.room_name}</span>
                          <div className="flex items-center gap-2 text-slate-400 flex-shrink-0">
                            <span>{r.slots_bought} bought</span>
                            <span>·</span>
                            <span className="text-emerald-400">{r.wins}W</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[9px] text-slate-500 leading-snug">
                    Win % ≈ slots-bought × 10% (10 slots per draw). Wins recycle XRP back into the treasury — they don&apos;t cost real money.
                  </p>
                </div>
              )}
            </div>
            {/* Group Chat ID — where Fireball shout-outs and community announcements post */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-white">Group Chat</p>
              <div className="rounded-xl bg-slate-800/50 border border-slate-700/50 p-3">
                <div className="flex items-start gap-2">
                  <MessageCircle className="w-4 h-4 mt-0.5 text-sky-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white">Fireball shout-outs post to this chat</p>
                    <p className="text-[10px] text-slate-500 leading-snug mt-0.5">
                      Add your bot to the group and paste the chat ID (e.g. <span className="font-mono text-slate-400">-1001234567890</span>).
                      Empty = disabled. Chat ID overrides the <span className="font-mono">TELEGRAM_GROUP_CHAT_ID</span> env var if set.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Input
                    data-testid="group-chat-id-input"
                    value={groupChatIdDraft}
                    onChange={(e) => setGroupChatIdDraft(e.target.value)}
                    placeholder="-1001234567890"
                    className="flex-1 h-9 text-xs bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 font-mono"
                  />
                  <Button
                    data-testid="group-chat-id-save"
                    size="sm"
                    onClick={saveGroupChatId}
                    disabled={savingGroupChat || groupChatIdDraft === (settings?.group_chat_id || '')}
                    className="bg-sky-600 hover:bg-sky-700 h-9 text-xs disabled:opacity-40"
                  >
                    {savingGroupChat ? 'Saving…' : 'Save'}
                  </Button>
                </div>
                {settings?.group_chat_id && (
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <p className="text-[10px] text-emerald-400">
                      ✓ Active — announcements will fire on next Fireball match
                    </p>
                    <Button
                      data-testid="group-chat-test-message"
                      size="sm"
                      variant="outline"
                      onClick={sendGroupTestMessage}
                      disabled={testingGroupChat}
                      className="h-7 text-[10px] border-sky-700/60 text-sky-300 hover:bg-sky-900/30 hover:text-sky-200 bg-transparent flex-shrink-0"
                    >
                      {testingGroupChat ? 'Sending…' : 'Send test message'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-white">FUZZY Memecoin</p>
              <div className="rounded-xl bg-slate-800/50 border border-amber-500/20 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-amber-300">🍯 Fuzzy's Honey Pot</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {fuzzyStatus
                        ? `${fuzzyStatus.fuzzy_rooms} FUZZY room${fuzzyStatus.fuzzy_rooms === 1 ? '' : 's'} · fees collected: ${(fuzzyStatus.total_fees_fuzzy || 0).toLocaleString()} FUZZY`
                        : 'Loading status…'}
                    </p>
                  </div>
                  {fuzzyStatus && (
                    <span data-testid="fuzzy-trustline-status" className={`text-[10px] font-bold px-2 py-1 rounded-md ${fuzzyStatus.trustline ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>
                      {fuzzyStatus.trustline ? '✓ Trustline active' : 'No trustline'}
                    </span>
                  )}
                </div>
                {fuzzyStatus && !fuzzyStatus.trustline && (
                  <Button
                    data-testid="fuzzy-setup-trustline"
                    size="sm"
                    onClick={setupFuzzyTrustline}
                    disabled={fuzzyLoading}
                    className="w-full bg-amber-600 hover:bg-amber-700 h-9 text-xs"
                  >
                    {fuzzyLoading ? 'Submitting…' : 'Set up FUZZY trustline on platform wallet'}
                  </Button>
                )}
                {fuzzyStatus?.trustline && fuzzyStatus.platform_fuzzy_balance !== null && (
                  <p className="text-[10px] text-slate-400">Platform FUZZY balance: <span className="text-amber-300 font-bold">{Number(fuzzyStatus.platform_fuzzy_balance).toLocaleString()}</span></p>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    data-testid="fuzzy-room-name-input"
                    value={fuzzyRoomName}
                    onChange={(e) => setFuzzyRoomName(e.target.value)}
                    placeholder="New FUZZY room name"
                    className="flex-1 h-9 text-xs bg-slate-900 border-slate-700 text-white placeholder:text-slate-600"
                  />
                  <Button
                    data-testid="fuzzy-create-room"
                    size="sm"
                    onClick={createFuzzyRoom}
                    disabled={fuzzyLoading || !fuzzyRoomName.trim()}
                    className="bg-amber-600 hover:bg-amber-700 h-9 text-xs disabled:opacity-40"
                  >
                    Create
                  </Button>
                </div>
                <Button
                  data-testid="fuzzy-announce-launch"
                  size="sm"
                  onClick={announceHoneyPot}
                  disabled={fuzzyAnnouncing}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 h-9 text-xs font-bold"
                >
                  {fuzzyAnnouncing ? 'Posting…' : '📣 Announce Honey Pot to group'}
                </Button>
                <p className="text-[10px] text-slate-500 leading-snug">
                  FUZZY rooms are first-buyer-priced: the first bear in sets the price per slot for that session. Deposits use the same address + tag as XRP.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-white">Housekeeping</p>
              <Button
                data-testid="cleanup-test-data"
                size="sm"
                variant="outline"
                onClick={cleanupTestData}
                disabled={cleaningTestData}
                className="w-full h-9 text-xs border-red-800/60 text-red-300 hover:bg-red-900/20 hover:text-red-200 bg-transparent"
              >
                {cleaningTestData ? 'Cleaning…' : '🧹 Remove test rooms & ghost players'}
              </Button>
              <p className="text-[10px] text-slate-500 leading-snug">
                Deletes rooms named TEST_/PYTEST_ (with their sessions, buys and escrows) plus ghost player accounts and their slot buys. Ghost mode stays off.
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Treasury Actions</p>
                {treasury?.treasury && (
                  <span className="text-[10px] text-slate-400">
                    Fees: <span className="text-emerald-300 font-semibold">{Number(treasury.treasury.total_fees || 0).toFixed(4)} XRP</span>
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="treasury-withdraw-btn"
                  size="sm"
                  onClick={() => setShowWithdrawDialog(true)}
                  disabled={!treasury?.treasury?.total_fees}
                  className="bg-emerald-600/80 hover:bg-emerald-700 text-xs h-9 disabled:opacity-40"
                >
                  <Wallet className="w-3.5 h-3.5 mr-1" /> Withdraw
                </Button>
                <Button
                  data-testid="treasury-reconcile-btn"
                  size="sm"
                  onClick={openReconcile}
                  className="bg-sky-600/80 hover:bg-sky-700 text-xs h-9"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reconcile
                </Button>
                <Button size="sm" onClick={() => setShowBuybackDialog(true)} className="bg-amber-600/80 hover:bg-amber-700 text-xs h-9">
                  <TrendingUp className="w-3.5 h-3.5 mr-1" /> $GHOST Buyback
                </Button>
                <Button size="sm" onClick={() => setShowNftDialog(true)} className="bg-purple-600/80 hover:bg-purple-700 text-xs h-9">
                  <ShoppingBag className="w-3.5 h-3.5 mr-1" /> Buy NFT
                </Button>
              </div>
              {treasuryHistory.length > 0 && (
                <div className="pt-1 space-y-1">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Recent withdrawals</p>
                  {treasuryHistory.slice(0, 5).map((w) => (
                    <div key={w.tx_id} data-testid={`treasury-withdraw-row-${w.tx_id.slice(0,8)}`} className="bg-slate-800/40 rounded-lg p-2 text-[10px] flex justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-emerald-300 font-semibold">−{Number(w.amount).toFixed(4)} XRP</p>
                        <p className="text-slate-400 font-mono truncate">{w.destination}</p>
                        {w.note && <p className="text-slate-500 italic truncate">{w.note}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-slate-500">{new Date(w.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</p>
                        {w.tx_hash && (
                          <a
                            href={`https://xrpscan.com/tx/${w.tx_hash}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-400 hover:underline text-[9px] font-mono"
                          >
                            {w.tx_hash.slice(0, 8)}…
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Buyback History */}
            {buybackHistory && (buybackHistory.buybacks.length > 0 || buybackHistory.nft_purchases.length > 0) && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-white">History</p>
                {buybackHistory.buybacks.map(b => (
                  <div key={b.buyback_id} className="bg-slate-800/40 rounded-lg p-2.5 text-xs">
                    <div className="flex justify-between"><span className="text-amber-300">$GHOST Buyback</span><span className="text-white font-semibold">{b.amount_xrp} XRP</span></div>
                    <p className="text-[10px] text-slate-500 mt-0.5">{b.wallet_address?.slice(0, 12)}... | {b.status}</p>
                  </div>
                ))}
                {buybackHistory.nft_purchases.map(n => (
                  <div key={n.purchase_id} className="bg-slate-800/40 rounded-lg p-2.5 text-xs">
                    <div className="flex justify-between"><span className="text-purple-300">NFT Purchase</span><span className="text-white font-semibold">{n.amount_xrp} XRP</span></div>
                    <p className="text-[10px] text-slate-500 mt-0.5">{n.nft_id} | {n.status}</p>
                  </div>
                ))}
              </div>
            )}
            {/* Orphan Deposits — untagged on-chain inbounds that need manual routing */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Orphan Deposits</p>
                <Button
                  data-testid="refresh-orphans-btn"
                  size="sm"
                  variant="outline"
                  onClick={fetchOrphans}
                  disabled={orphansLoading}
                  className="border-slate-600 text-slate-300 text-[11px] h-7 px-2"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${orphansLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 leading-snug">
                On-chain XRP payments to the platform wallet that arrived without a destination tag. Credit each one to the intended user's balance.
              </p>
              {orphans.length === 0 ? (
                <div className="text-center py-4 text-[11px] text-slate-500 bg-slate-800/30 border border-dashed border-slate-700 rounded-lg">
                  {orphansLoading ? 'Scanning ledger…' : 'No untagged deposits found on-chain.'}
                </div>
              ) : (
                <div className="space-y-2">
                  {orphans.map((o) => (
                    <div
                      key={o.tx_hash}
                      data-testid={`orphan-${o.tx_hash.slice(0, 8)}`}
                      className={`rounded-lg border p-2.5 space-y-1.5 ${
                        o.credited
                          ? 'bg-emerald-500/5 border-emerald-500/25'
                          : 'bg-amber-500/5 border-amber-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-bold text-white">{o.amount_xrp.toFixed(6)} XRP</span>
                        {o.credited ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-semibold">
                            Credited → @{o.credited_to_username || o.credited_to_user_id?.slice(0, 6)}
                          </span>
                        ) : (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-semibold">
                            Unassigned
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 space-y-0.5">
                        <p>
                          From{' '}
                          <span className="font-mono text-slate-300">{o.sender?.slice(0, 10)}…{o.sender?.slice(-4)}</span>
                          {' · '}
                          {o.timestamp ? new Date(o.timestamp + 'Z').toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </p>
                        <div className="flex items-center gap-2">
                          <a
                            href={`https://xrpscan.com/tx/${o.tx_hash}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-400 hover:underline font-mono"
                          >
                            {o.tx_hash.slice(0, 12)}…
                          </a>
                          <button
                            type="button"
                            onClick={() => { navigator.clipboard.writeText(o.tx_hash); toast.success('Tx hash copied'); }}
                            className="text-slate-500 hover:text-slate-300"
                            title="Copy full tx hash"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {!o.credited && (
                        <Button
                          data-testid={`credit-orphan-btn-${o.tx_hash.slice(0, 8)}`}
                          size="sm"
                          onClick={() => { setOrphanCreditFor(o); setOrphanCreditUser(''); setOrphanCreditNote(''); }}
                          className="w-full bg-amber-600 hover:bg-amber-700 h-8 text-xs"
                        >
                          <Gift className="w-3 h-3 mr-1" /> Credit to user
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

          </TabsContent>

          {/* Escrows Tab */}
          <TabsContent value="escrows" className="mt-3 space-y-3">
            {escrows.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-500">No escrows</div>
            ) : (
              escrows.map(escrow => (
                <Card key={escrow.escrow_id} className="bg-slate-800/60 border-slate-700">
                  <div className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-xs font-semibold text-white">{escrow.prize_description}</p>
                        <p className="text-[10px] text-slate-500">{escrow.prize_type} | {escrow.escrow_amount?.toFixed(2)} XRP</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        escrow.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300' :
                        escrow.status === 'disputed' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'
                      }`}>{escrow.status}</span>
                    </div>
                    {escrow.status === 'disputed' && (
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" onClick={() => resolveEscrow(escrow.escrow_id, 'release')} className="flex-1 bg-emerald-600 text-xs h-7">Release</Button>
                        <Button size="sm" onClick={() => resolveEscrow(escrow.escrow_id, 'refund')} className="flex-1 bg-red-600 text-xs h-7">Refund</Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))
            )}
          </TabsContent>
          {/* Slots Tab */}
          <TabsContent value="slots" data-testid="tab-slots" className="mt-3 space-y-3">
            <div className="flex gap-2">
              <Button
                data-testid="refresh-slots-btn"
                size="sm"
                onClick={fetchOccupiedSlots}
                disabled={slotsLoading}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 text-xs h-9"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${slotsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                data-testid="cleanup-stale-btn"
                size="sm"
                onClick={handleCleanupStale}
                className="flex-1 bg-amber-600/80 hover:bg-amber-700 text-xs h-9"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Bulk Clean Stale
              </Button>
            </div>
            <p className="text-[10px] text-slate-500 leading-snug">
              View every occupied slot across active sessions. <span className="text-emerald-400">Clear & Refund</span> returns XRP to the user. <span className="text-red-400">Clear (no refund)</span> for ghosts/system. <span className="text-amber-400">Bulk Clean Stale</span> removes purchases tied to deleted users or stale game sessions.
            </p>
            {occupiedSlots.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-500" data-testid="slots-empty">
                {slotsLoading ? 'Loading...' : 'No occupied slots'}
              </div>
            ) : (
              <div className="space-y-2">
                {occupiedSlots.map((s) => (
                  <div
                    key={s.purchase_id}
                    data-testid={`slot-row-${s.purchase_id.slice(0, 8)}`}
                    className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-orange-500/20 text-orange-300 text-sm font-black">
                            {s.slot_number}
                          </span>
                          <span className="text-xs font-bold text-white truncate">{s.room_name}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${
                            s.draw_type === 'midday' ? 'bg-amber-500/15 text-amber-300' : 'bg-indigo-500/15 text-indigo-300'
                          }`}>{s.draw_type}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-slate-400">
                          <span>by</span>
                          <span className={`font-medium ${s.username === '<deleted>' ? 'text-red-400' : 'text-white'}`}>
                            {s.username}
                          </span>
                          {(s.is_ghost || s.is_system) && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-slate-700 text-slate-400">
                              <Bot className="w-2 h-2" /> ghost
                            </span>
                          )}
                          <span className="ml-auto text-slate-500">{s.amount_paid + s.fee_paid} XRP</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <Button
                        data-testid={`clear-refund-${s.purchase_id.slice(0, 8)}`}
                        size="sm"
                        onClick={() => handleClearSlot(s.purchase_id, true)}
                        disabled={s.is_ghost || s.is_system || s.username === '<deleted>'}
                        className="flex-1 bg-emerald-600/80 hover:bg-emerald-700 text-[11px] h-8"
                      >
                        Clear & Refund
                      </Button>
                      <Button
                        data-testid={`clear-no-refund-${s.purchase_id.slice(0, 8)}`}
                        size="sm"
                        onClick={() => handleClearSlot(s.purchase_id, false)}
                        variant="outline"
                        className="flex-1 border-red-500/40 text-red-300 hover:bg-red-500/10 text-[11px] h-8"
                      >
                        Clear (no refund)
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          {/* Rooms Tab */}
          <TabsContent value="rooms" data-testid="tab-rooms" className="mt-3 space-y-3">
            <div className="flex gap-2">
              <Button
                data-testid="refresh-rooms-btn"
                size="sm"
                onClick={fetchAdminRoomsAndAuctions}
                disabled={roomsLoading}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 text-xs h-9"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${roomsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                data-testid="expire-idle-btn"
                size="sm"
                onClick={handleExpireIdle}
                className="flex-1 bg-amber-600/80 hover:bg-amber-700 text-xs h-9"
              >
                <Clock className="w-3.5 h-3.5 mr-1" /> Expire Idle (7d)
              </Button>
            </div>
            <div className="flex gap-1.5 text-[11px]">
              {[
                { v: 'all', label: 'All' },
                { v: 'hosted', label: 'Hosted' },
                { v: 'admin', label: 'XRP Pool' },
                { v: 'auction', label: 'Auctions' },
                { v: 'cancelled', label: 'Cancelled' },
              ].map(f => (
                <button
                  key={f.v}
                  data-testid={`room-filter-${f.v}`}
                  onClick={() => setRoomFilter(f.v)}
                  className={`px-2.5 py-1 rounded-full font-medium ${
                    roomFilter === f.v ? 'bg-purple-500/25 text-purple-200' : 'bg-slate-800/40 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 leading-snug">
              Force-cancel any room or auction. <span className="text-emerald-400">Cancelling refunds all active buyers</span> automatically. Idle hosted rooms (no players for 7+ days) auto-expire daily at 4am CT.
            </p>

            {/* Auctions */}
            {(roomFilter === 'all' || roomFilter === 'auction' || roomFilter === 'cancelled') && adminAuctions
              .filter(a => roomFilter !== 'cancelled' || ['cancelled', 'unsold'].includes(a.status))
              .map((a) => (
                <div
                  key={a.auction_id}
                  data-testid={`admin-auction-row-${a.auction_id.slice(0, 8)}`}
                  className="bg-slate-800/40 border border-indigo-500/20 rounded-xl p-3"
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 font-bold">AUCTION</span>
                        <span className="text-xs font-bold text-white truncate">{a.room_name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${
                          a.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' :
                          a.status === 'completed' ? 'bg-blue-500/20 text-blue-300' :
                          a.status === 'unsold' ? 'bg-slate-600/40 text-slate-300' :
                          'bg-red-500/20 text-red-300'
                        }`}>{a.status}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                        <span>by @{a.host_username}</span>
                        <span>·</span>
                        <span>{a.bid_count || 0} bids</span>
                        <span>·</span>
                        <span className="text-orange-300 font-semibold">{(a.current_bid || a.starting_bid).toFixed(2)} XRP</span>
                      </div>
                    </div>
                    {a.status === 'active' && (
                      <Button
                        data-testid={`cancel-auction-${a.auction_id.slice(0, 8)}`}
                        size="sm"
                        onClick={() => handleCancelAuction(a.auction_id, a.room_name)}
                        className="bg-red-600/80 hover:bg-red-700 text-[10px] h-7 px-2"
                      >
                        <X className="w-2.5 h-2.5 mr-0.5" /> Cancel
                      </Button>
                    )}
                  </div>
                </div>
              ))}

            {/* Game Rooms (admin + hosted) */}
            {adminRooms
              .filter(r => {
                if (roomFilter === 'all') return true;
                if (roomFilter === 'hosted') return r.is_hosted;
                if (roomFilter === 'admin') return r.is_admin_room;
                if (roomFilter === 'cancelled') return ['cancelled', 'expired'].includes(r.status);
                return true;
              })
              .map((r) => (
                <div
                  key={r.game_id}
                  data-testid={`admin-room-row-${r.game_id.slice(0, 8)}`}
                  className={`bg-slate-800/40 border rounded-xl p-3 ${
                    r.is_admin_room ? 'border-orange-500/20' : 'border-purple-500/20'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[8px] px-1.5 py-0.5 rounded font-bold ${
                          r.is_admin_room ? 'bg-orange-500/20 text-orange-300' : 'bg-purple-500/20 text-purple-300'
                        }`}>
                          {r.is_admin_room ? 'XRP POOL' : (r.prize_type || 'HOSTED').toUpperCase()}
                        </span>
                        <span className="text-xs font-bold text-white truncate">{r.room_name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${
                          r.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' :
                          r.status === 'cancelled' ? 'bg-red-500/20 text-red-300' :
                          r.status === 'expired' ? 'bg-amber-500/20 text-amber-300' :
                          'bg-slate-600/40 text-slate-300'
                        }`}>{r.status}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400 flex-wrap">
                        {r.host_username && <><span>by @{r.host_username}</span><span>·</span></>}
                        <span>{r.entry_price} XRP entry</span>
                        <span>·</span>
                        <span className="text-orange-300 font-semibold">{r.slots_sold || 0} slots sold</span>
                        {r.min_players >= 2 && (<><span>·</span><span>min {r.min_players}</span></>)}
                        {r.idle_days != null && r.slots_sold === 0 && r.status === 'active' && (
                          <><span>·</span><span className={r.idle_days >= 7 ? 'text-red-400' : 'text-slate-500'}>{r.idle_days}d idle</span></>
                        )}
                      </div>
                      {r.host_wallet && (
                        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-slate-500 font-mono">
                          <Wallet className="w-2.5 h-2.5" />
                          <span data-testid={`room-host-wallet-${r.game_id.slice(0, 8)}`} className="truncate">{r.host_wallet}</span>
                          <button
                            onClick={() => copyToClipboard(r.host_wallet, 'Host wallet copied')}
                            className="text-slate-500 hover:text-slate-300 transition flex-shrink-0"
                            aria-label="Copy host wallet"
                          >
                            <Copy className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    {r.status === 'active' && (
                      <Button
                        data-testid={`cancel-room-${r.game_id.slice(0, 8)}`}
                        size="sm"
                        onClick={() => handleCancelRoom(r.game_id, r.room_name)}
                        className="bg-red-600/80 hover:bg-red-700 text-[10px] h-7 px-2"
                      >
                        <X className="w-2.5 h-2.5 mr-0.5" /> Cancel
                      </Button>
                    )}
                    {r.status === 'cancelled' && (
                      <Button
                        data-testid={`reactivate-room-${r.game_id.slice(0, 8)}`}
                        size="sm"
                        onClick={() => handleReactivateRoom(r.game_id, r.room_name)}
                        className="bg-emerald-600/80 hover:bg-emerald-700 text-[10px] h-7 px-2"
                      >
                        Re-enable
                      </Button>
                    )}
                  </div>
                </div>
              ))}

            {(adminRooms.length + adminAuctions.length) === 0 && !roomsLoading && (
              <div className="text-center py-8 text-sm text-slate-500" data-testid="rooms-empty">No rooms or auctions</div>
            )}
          </TabsContent>
          {/* Reports Tab */}
          <TabsContent value="reports" data-testid="tab-reports" className="mt-3 space-y-3">
            <div className="flex gap-1.5 text-[11px]">
              {[{v:'open',l:'Open'},{v:'reviewing',l:'Reviewing'},{v:'resolved',l:'Resolved'},{v:'dismissed',l:'Dismissed'},{v:'all',l:'All'}].map(f => (
                <button
                  key={f.v}
                  data-testid={`reports-filter-${f.v}`}
                  onClick={() => { setReportsFilter(f.v); fetchReports(f.v); }}
                  className={`px-2.5 py-1 rounded-full font-medium ${reportsFilter === f.v ? 'bg-amber-500/25 text-amber-200' : 'bg-slate-800/40 text-slate-400 hover:text-slate-200'}`}
                >
                  {f.l}
                </button>
              ))}
            </div>

            {reports.length === 0 ? (
              <p data-testid="reports-empty" className="text-center py-6 text-sm text-slate-500">No reports</p>
            ) : (
              reports.map((r) => (
                <div
                  key={r.report_id}
                  data-testid={`report-row-${r.report_id.slice(0,8)}`}
                  className="bg-slate-800/40 border border-amber-500/20 rounded-xl p-3 space-y-1.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold uppercase">{r.reason.replace(/_/g, ' ')}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-700/40 text-slate-300">{r.target_type.replace('_', ' ')}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${
                          r.status === 'open' ? 'bg-red-500/20 text-red-300' :
                          r.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-300' :
                          r.status === 'dismissed' ? 'bg-slate-600/40 text-slate-300' :
                          'bg-amber-500/20 text-amber-300'
                        }`}>{r.status}</span>
                      </div>
                      <p className="text-xs text-white font-medium truncate mt-1">{r.target_name || r.target_id}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">by @{r.reporter_username}</p>
                      {r.description && (
                        <p className="text-[10px] text-slate-300 mt-1 italic line-clamp-3">"{r.description}"</p>
                      )}
                    </div>
                  </div>
                  {r.status === 'open' && (
                    <div className="flex gap-1.5">
                      <Button
                        data-testid={`report-resolve-${r.report_id.slice(0,8)}`}
                        size="sm"
                        onClick={() => handleResolveReport(r.report_id, 'resolved')}
                        className="flex-1 bg-emerald-600/80 hover:bg-emerald-700 text-[10px] h-7"
                      >
                        <Check className="w-2.5 h-2.5 mr-1" /> Resolved
                      </Button>
                      <Button
                        data-testid={`report-dismiss-${r.report_id.slice(0,8)}`}
                        size="sm"
                        onClick={() => handleResolveReport(r.report_id, 'dismissed')}
                        variant="outline"
                        className="flex-1 border-slate-600 text-slate-300 text-[10px] h-7"
                      >
                        Dismiss
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" data-testid="tab-users" className="mt-3 space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <Input
                  data-testid="users-search-input"
                  placeholder="Search by username, email, or wallet"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchUsers(userSearch); }}
                  className="bg-slate-800/60 border-slate-700 text-white h-9 text-xs pl-7"
                />
              </div>
              <Button
                data-testid="users-search-btn"
                size="sm"
                onClick={() => fetchUsers(userSearch)}
                className="bg-cyan-600/80 hover:bg-cyan-700 text-xs h-9 px-3"
              >
                Search
              </Button>
            </div>
            <p className="text-[10px] text-slate-500">
              {usersLoading ? 'Loading…' : `${users.length} of ${usersTotal} users`}
            </p>
            <div className="space-y-2">
              {users.map((u) => (
                <div
                  key={u.user_id}
                  data-testid={`admin-user-row-${u.user_id.slice(0, 8)}`}
                  className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-bold text-white truncate">{u.username || '<no username>'}</span>
                        {u.is_admin && <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 font-bold">ADMIN</span>}
                        {u.is_system && <span className="text-[8px] px-1.5 py-0.5 rounded bg-slate-600/40 text-slate-300 font-bold">SYSTEM</span>}
                      </div>
                      {u.email && <p className="text-[10px] text-slate-400 truncate mt-0.5">{u.email}</p>}
                      {u.telegram_username && <p className="text-[10px] text-cyan-300 mt-0.5">@{u.telegram_username}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-emerald-400">{(u.balance ?? 0).toFixed(2)}</p>
                      <p className="text-[9px] text-slate-500 uppercase tracking-wider">XRP</p>
                    </div>
                  </div>
                  {u.xrpl_address ? (
                    <div className="flex items-center gap-1.5 mt-2 text-[10px] text-slate-400 font-mono bg-slate-900/40 rounded px-2 py-1">
                      <Wallet className="w-2.5 h-2.5 text-indigo-400 flex-shrink-0" />
                      <span className="truncate" data-testid={`user-wallet-${u.user_id.slice(0, 8)}`}>{u.xrpl_address}</span>
                      <button
                        onClick={() => copyToClipboard(u.xrpl_address, 'Wallet copied')}
                        className="text-slate-500 hover:text-emerald-400 flex-shrink-0"
                        aria-label="Copy wallet"
                      >
                        <Copy className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-[10px] text-slate-600 italic mt-2">No XRPL wallet linked</p>
                  )}
                </div>
              ))}
              {!usersLoading && users.length === 0 && (
                <p data-testid="users-empty" className="text-center py-8 text-sm text-slate-500">
                  {userSearch ? 'No users match your search' : 'No users yet'}
                </p>
              )}
            </div>
          </TabsContent>

          {/* Host Applications Tab */}
          <TabsContent value="host-apps" data-testid="tab-host-apps" className="mt-3 space-y-3">
            {/* Auto-reward Auction Champion toggle */}
            <div className="rounded-xl border border-yellow-500/25 bg-gradient-to-br from-yellow-500/10 to-amber-500/5 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-yellow-200">🏅 Auto-reward Auction Champions</p>
                  <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">
                    Automatically credits any user with XRP the moment they cross into Auction Champion (5 wins). Set-and-forget appreciation.
                  </p>
                </div>
                <button
                  data-testid="auto-reward-toggle"
                  onClick={() => saveAutoReward(!autoReward.enabled, autoReward.amount_xrp)}
                  disabled={autoRewardLoading}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    autoReward.enabled ? 'bg-yellow-500' : 'bg-slate-700'
                  }`}
                  aria-pressed={autoReward.enabled}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    autoReward.enabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              {autoReward.enabled && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="number"
                    min="0.1"
                    max="100"
                    step="0.5"
                    value={autoReward.amount_xrp}
                    onChange={(e) => setAutoReward({ ...autoReward, amount_xrp: parseFloat(e.target.value) || 0 })}
                    data-testid="auto-reward-amount-input"
                    className="flex-1 min-w-0 bg-slate-900/60 border border-yellow-500/30 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-yellow-500/60"
                  />
                  <span className="text-[10px] text-slate-400">XRP per unlock</span>
                  <Button
                    data-testid="auto-reward-save"
                    onClick={() => saveAutoReward(true, autoReward.amount_xrp)}
                    disabled={autoRewardLoading || autoReward.amount_xrp <= 0 || autoReward.amount_xrp > 100}
                    size="sm"
                    className="bg-yellow-600 hover:bg-yellow-700 h-7 px-2 text-[10px] font-bold"
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>

            {/* Explainer + direct-grant CTA */}
            <div className="rounded-xl border border-purple-500/25 bg-gradient-to-br from-purple-500/10 to-indigo-500/5 p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-bold text-purple-200">Allow externals to list rooms</p>
                  <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">
                    Approve pending applications below, revoke privileges, or directly grant host access by username / wallet address.
                  </p>
                </div>
                <Button
                  data-testid="grant-host-direct-button"
                  onClick={handleGrantHost}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 h-8 px-3 text-[11px] font-bold flex-shrink-0"
                >
                  <Check className="w-3 h-3 mr-1" /> Allow user
                </Button>
              </div>
            </div>

            {/* Applicant / User search — find anyone even if they've never applied */}
            <div className="rounded-xl border border-slate-700/40 bg-slate-800/40 p-3 space-y-2">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Find a user</p>
              <div className="flex gap-2">
                <input
                  data-testid="host-user-search-input"
                  type="text"
                  placeholder="@username, email, or wallet (r...)"
                  value={userSearchQ}
                  onChange={(e) => setUserSearchQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleUserSearch(); }}
                  className="flex-1 min-w-0 bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500/50"
                />
                <Button
                  data-testid="host-user-search-button"
                  onClick={handleUserSearch}
                  disabled={userSearchLoading}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 h-8 px-3 text-[11px] font-bold flex-shrink-0"
                >
                  {userSearchLoading ? '…' : 'Search'}
                </Button>
                {userSearchResults !== null && (
                  <Button
                    data-testid="host-user-search-clear"
                    onClick={() => { setUserSearchQ(''); setUserSearchResults(null); }}
                    size="sm"
                    variant="outline"
                    className="h-8 px-2 text-[11px] border-slate-700 bg-slate-800/50 hover:bg-slate-800 text-slate-300 flex-shrink-0"
                  >
                    Clear
                  </Button>
                )}
              </div>

              {userSearchResults !== null && userSearchResults.length === 0 && !userSearchLoading && (
                <p data-testid="host-user-search-empty" className="text-[11px] text-slate-500 italic py-1">
                  No users match &quot;{userSearchQ}&quot;
                </p>
              )}

              {userSearchResults !== null && userSearchResults.length > 0 && (
                <div className="space-y-2 pt-1">
                  {userSearchResults.map((u) => (
                    <div
                      key={u.user_id}
                      data-testid={`host-user-search-result-${u.user_id.slice(0, 8)}`}
                      className="rounded-lg border border-slate-700/50 bg-slate-900/40 p-2.5 space-y-2"
                    >
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-xs font-bold text-white truncate">@{u.username}</p>
                            {u.can_host && (
                              <span className="text-[9px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-1.5 py-0.5">HOST</span>
                            )}
                            {u.pending_application_id && !u.can_host && (
                              <span className="text-[9px] font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-full px-1.5 py-0.5">WAITING</span>
                            )}
                            {u.is_system && (
                              <span className="text-[9px] font-bold text-sky-300 bg-sky-500/15 border border-sky-500/30 rounded-full px-1.5 py-0.5">SYSTEM</span>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-500 truncate">{u.email}</p>
                          {u.xrpl_address && (
                            <button
                              onClick={() => copyToClipboard(u.xrpl_address, 'Wallet address copied')}
                              className="text-[10px] font-mono text-emerald-300 hover:text-emerald-200 truncate max-w-full"
                              title="Copy XRPL address"
                            >
                              {u.xrpl_address.slice(0, 8)}…{u.xrpl_address.slice(-6)}
                            </button>
                          )}
                          {/* Badges — emoji chips */}
                          {u.badges && u.badges.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`host-user-badges-${u.user_id.slice(0, 8)}`}>
                              {u.badges.map((b) => (
                                <span
                                  key={b.id}
                                  className="inline-flex items-center gap-0.5 text-[9px] font-bold bg-slate-800/80 border border-slate-700 rounded-full px-1.5 py-0.5 text-slate-200"
                                  title={b.name}
                                >
                                  {b.emoji} {b.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          {u.can_host ? (
                            <Button
                              data-testid={`host-user-revoke-${u.user_id.slice(0, 8)}`}
                              onClick={() => revokeHostForUser(u.user_id, u.username)}
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-[10px] border-red-500/40 bg-red-500/5 hover:bg-red-500/15 text-red-300"
                            >
                              Revoke
                            </Button>
                          ) : (
                            <Button
                              data-testid={`host-user-grant-${u.user_id.slice(0, 8)}`}
                              onClick={() => grantHostToUser(u.user_id, u.username)}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-[10px] font-bold"
                            >
                              <Check className="w-3 h-3 mr-1" /> Allow
                            </Button>
                          )}
                          <Button
                            data-testid={`host-user-reward-${u.user_id.slice(0, 8)}`}
                            onClick={() => rewardUser(u.user_id, u.username, u.badges?.some(b => b.id === 'auction_champion') ? '10' : '5')}
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[10px] border-yellow-500/40 bg-yellow-500/5 hover:bg-yellow-500/15 text-yellow-300"
                            title="Send XRP reward directly to this user"
                          >
                            <Gift className="w-3 h-3 mr-1" /> Reward
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 bg-slate-800/40 border border-slate-700/40 rounded-xl p-1">
              {['pending', 'approved', 'denied', 'all'].map((s) => (
                <button
                  key={s}
                  data-testid={`host-apps-filter-${s}`}
                  onClick={() => fetchHostApps(s)}
                  className={`flex-1 text-[10px] uppercase tracking-wide py-1.5 rounded-lg transition-colors ${
                    hostAppsFilter === s
                      ? 'bg-purple-500/30 text-purple-200 font-bold'
                      : 'text-slate-400 hover:bg-slate-700/30'
                  }`}
                >
                  {s === 'pending' ? 'Waiting' : s}
                </button>
              ))}
            </div>

            {hostAppsLoading ? (
              <p className="text-center py-8 text-sm text-slate-500">Loading…</p>
            ) : hostApps.length === 0 ? (
              <p data-testid="host-apps-empty" className="text-center py-8 text-sm text-slate-500">
                No {hostAppsFilter === 'all' ? '' : hostAppsFilter + ' '}applications
              </p>
            ) : (
              <div className="space-y-2">
                {/* Bulk-action toolbar — only when there are pending applications */}
                {hostApps.some(a => a.status === 'pending') && (
                  <div
                    data-testid="host-apps-bulk-toolbar"
                    className="flex items-center gap-2 rounded-lg border border-slate-700/50 bg-slate-800/60 px-3 py-2"
                  >
                    <button
                      data-testid="host-apps-select-all"
                      onClick={toggleAllSelected}
                      className="text-[11px] text-slate-300 hover:text-white font-semibold px-2 py-1"
                    >
                      {hostApps.filter(a => a.status === 'pending').every(a => selectedApps[a.application_id])
                        ? 'Unselect all'
                        : 'Select all pending'}
                    </button>
                    <span className="text-[10px] text-slate-500 ml-auto">
                      {Object.keys(selectedApps).length} selected
                    </span>
                    <Button
                      data-testid="host-apps-bulk-approve"
                      onClick={() => handleBulkAction('approve')}
                      disabled={bulkLoading || Object.keys(selectedApps).length === 0}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2.5 text-[10px] font-bold"
                    >
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button
                      data-testid="host-apps-bulk-deny"
                      onClick={() => handleBulkAction('deny')}
                      disabled={bulkLoading || Object.keys(selectedApps).length === 0}
                      size="sm"
                      variant="outline"
                      className="h-7 px-2.5 text-[10px] font-bold border-red-500/40 bg-red-500/5 hover:bg-red-500/15 text-red-300"
                    >
                      <X className="w-3 h-3 mr-1" /> Deny
                    </Button>
                  </div>
                )}
                {hostApps.map((app) => (
                  <div
                    key={app.application_id}
                    data-testid={`host-app-${app.application_id.slice(0, 8)}`}
                    className={`border rounded-xl p-3 space-y-2 transition-colors ${
                      selectedApps[app.application_id]
                        ? 'bg-purple-500/10 border-purple-500/40'
                        : 'bg-slate-800/40 border-slate-700/40'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      {/* Checkbox — only functional on pending; disabled/hidden on decided rows */}
                      {app.status === 'pending' && (
                        <input
                          type="checkbox"
                          data-testid={`host-app-checkbox-${app.application_id.slice(0, 8)}`}
                          checked={!!selectedApps[app.application_id]}
                          onChange={() => toggleAppSelected(app.application_id)}
                          className="mt-1 w-4 h-4 accent-purple-500 cursor-pointer flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-white">@{app.username}</p>
                        <p className="text-[10px] text-slate-500 truncate">{app.email}</p>
                        {app.telegram_username && (
                          <p className="text-[10px] text-sky-400 truncate">tg: @{app.telegram_username}</p>
                        )}
                        {app.xrpl_address ? (
                          <button
                            data-testid={`host-app-wallet-${app.application_id.slice(0, 8)}`}
                            onClick={() => copyToClipboard(app.xrpl_address, 'Wallet address copied')}
                            className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-emerald-300 hover:text-emerald-200 truncate max-w-full"
                            title="Copy XRPL address"
                          >
                            {app.xrpl_address.slice(0, 8)}…{app.xrpl_address.slice(-6)}
                          </button>
                        ) : (
                          <p className="mt-1 text-[10px] text-slate-600 italic">No wallet linked</p>
                        )}
                      </div>
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                        app.status === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                        app.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                        'bg-red-500/20 text-red-300'
                      }`}>
                        {app.status === 'pending' ? 'waiting' : app.status}
                      </span>
                    </div>

                    <div className="bg-slate-900/40 rounded-lg p-2 space-y-1.5">
                      <p className="text-[10px] text-slate-500 uppercase">What they'd list</p>
                      <p className="text-xs text-slate-200 whitespace-pre-wrap">{app.reason}</p>
                    </div>
                    {app.experience && (
                      <div className="bg-slate-900/40 rounded-lg p-2 space-y-1.5">
                        <p className="text-[10px] text-slate-500 uppercase">Experience</p>
                        <p className="text-xs text-slate-300 whitespace-pre-wrap">{app.experience}</p>
                      </div>
                    )}

                    {app.denial_reason && (
                      <p className="text-[10px] text-red-300 bg-red-500/5 border border-red-500/20 rounded p-1.5">
                        Denied: {app.denial_reason}
                      </p>
                    )}

                    <p className="text-[9px] text-slate-600">
                      Submitted {new Date(app.created_at).toLocaleString()}
                      {app.reviewed_at && ` · reviewed ${new Date(app.reviewed_at).toLocaleString()}`}
                    </p>

                    {app.status === 'pending' && (
                      <div className="flex items-center gap-2 pt-1">
                        <Button
                          data-testid={`host-app-approve-${app.application_id.slice(0, 8)}`}
                          onClick={() => handleApproveHost(app.application_id)}
                          size="sm"
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                        >
                          <Check className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button
                          data-testid={`host-app-deny-${app.application_id.slice(0, 8)}`}
                          onClick={() => handleDenyHost(app.application_id)}
                          size="sm"
                          variant="outline"
                          className="flex-1 border-red-500/40 text-red-300 hover:bg-red-500/10 h-8 text-xs"
                        >
                          <X className="w-3 h-3 mr-1" /> Deny
                        </Button>
                      </div>
                    )}
                    {app.status === 'approved' && (
                      <Button
                        data-testid={`host-revoke-${app.application_id.slice(0, 8)}`}
                        onClick={() => handleRevokeHost(app.user_id, app.username)}
                        size="sm"
                        variant="outline"
                        className="w-full border-orange-500/40 text-orange-300 hover:bg-orange-500/10 h-8 text-xs"
                      >
                        Revoke Host Privileges
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Treasury Reconcile Dialog */}
      <Dialog open={showReconcileDialog} onOpenChange={setShowReconcileDialog}>
        <DialogContent data-testid="reconcile-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Reconcile Treasury</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Recompute total fees from the fee log and subtract past withdrawals.
            </DialogDescription>
          </DialogHeader>
          {reconcileLoading && !reconcilePreview ? (
            <div className="py-8 text-center text-sm text-slate-400">Scanning fee log…</div>
          ) : reconcilePreview ? (
            <div className="space-y-3 pt-2">
              <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                  <span className="text-slate-400">Current counter</span>
                  <span className="text-white font-mono">{reconcilePreview.old_total_fees.toFixed(4)} XRP</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Fees collected</span>
                  <span className="text-emerald-300 font-mono">+{reconcilePreview.breakdown.fees_collected.toFixed(4)} XRP</span>
                </div>
                <div className="text-[10px] text-slate-500 -mt-1 ml-1">from {reconcilePreview.breakdown.fee_log_entries} fee_log entries</div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Fees withdrawn</span>
                  <span className="text-orange-300 font-mono">−{reconcilePreview.breakdown.fees_withdrawn.toFixed(4)} XRP</span>
                </div>
                <div className="text-[10px] text-slate-500 -mt-1 ml-1">from {reconcilePreview.breakdown.withdrawal_entries} past withdrawals</div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-700">
                  <span className="text-white font-semibold">New counter</span>
                  <span data-testid="reconcile-new-value" className="text-sky-300 font-bold font-mono">{reconcilePreview.computed_total_fees.toFixed(4)} XRP</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-xs">Delta</span>
                  <span className={`text-xs font-mono ${reconcilePreview.delta >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                    {reconcilePreview.delta > 0 ? '+' : ''}{reconcilePreview.delta.toFixed(4)} XRP
                  </span>
                </div>
              </div>
              {Math.abs(reconcilePreview.delta) < 0.0001 ? (
                <p className="text-[11px] text-emerald-400 leading-snug text-center">
                  ✅ Counter already matches the fee log — no action needed.
                </p>
              ) : (
                <p className="text-[10px] text-amber-400 leading-snug">
                  This will replace the current counter with the computed value. An audit row is written so the change is reversible if you need to investigate.
                </p>
              )}
              <Button
                data-testid="reconcile-commit-btn"
                onClick={commitReconcile}
                disabled={reconcileLoading || Math.abs(reconcilePreview.delta) < 0.0001}
                className="w-full bg-sky-600 hover:bg-sky-700 h-10 disabled:opacity-40"
              >
                {reconcileLoading ? 'Reconciling…' : 'Commit Reconcile'}
              </Button>
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-red-400">Failed to load preview.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Orphan Deposit Credit Dialog */}
      <Dialog open={!!orphanCreditFor} onOpenChange={(o) => { if (!o) setOrphanCreditFor(null); }}>
        <DialogContent data-testid="orphan-credit-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Credit Orphan Deposit</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Assign this untagged on-chain payment to a user's in-app balance.
            </DialogDescription>
          </DialogHeader>
          {orphanCreditFor && (
            <div className="space-y-3 pt-2">
              <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-2.5 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-400">Amount</span>
                  <span className="text-emerald-300 font-bold">{orphanCreditFor.amount_xrp} XRP</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Sender</span>
                  <span className="text-slate-200 font-mono">{orphanCreditFor.sender?.slice(0, 10)}…</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">TX</span>
                  <a
                    href={`https://xrpscan.com/tx/${orphanCreditFor.tx_hash}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-400 hover:underline font-mono"
                  >
                    {orphanCreditFor.tx_hash.slice(0, 14)}…
                  </a>
                </div>
              </div>
              <Input
                data-testid="orphan-credit-username-input"
                placeholder="Username (e.g. fuzzy123321)"
                value={orphanCreditUser}
                onChange={(e) => setOrphanCreditUser(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
                autoFocus
              />
              <Input
                data-testid="orphan-credit-note-input"
                placeholder="Note (optional — audit trail)"
                value={orphanCreditNote}
                onChange={(e) => setOrphanCreditNote(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
              />
              <p className="text-[10px] text-slate-500 leading-snug">
                Idempotent: the same tx hash can never be credited twice, even if you re-click.
              </p>
              <Button
                data-testid="orphan-credit-confirm-btn"
                onClick={submitOrphanCredit}
                disabled={orphanCreditLoading || !orphanCreditUser.trim()}
                className="w-full bg-amber-600 hover:bg-amber-700 h-10"
              >
                {orphanCreditLoading ? 'Crediting…' : `Credit ${orphanCreditFor.amount_xrp} XRP`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Treasury Withdraw Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent data-testid="treasury-withdraw-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Withdraw Treasury Fees</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Send platform-fee XRP from the treasury wallet to any XRPL address. Real on-chain transaction.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-2.5 flex justify-between items-center">
              <span className="text-xs text-slate-400">Available fees</span>
              <span data-testid="treasury-available-fees" className="text-sm font-bold text-emerald-300">
                {Number(treasury?.treasury?.total_fees || 0).toFixed(4)} XRP
              </span>
            </div>
            <Input
              data-testid="withdraw-amount-input"
              type="number"
              placeholder="Amount (XRP)"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <div className="flex gap-1">
              {[0.25, 0.5, 1.0].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  data-testid={`withdraw-pct-${pct}`}
                  onClick={() => setWithdrawAmount(((treasury?.treasury?.total_fees || 0) * pct).toFixed(4))}
                  className="flex-1 text-[10px] py-1 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
                >
                  {pct === 1 ? 'MAX' : `${pct * 100}%`}
                </button>
              ))}
            </div>
            <Input
              data-testid="withdraw-dest-input"
              placeholder="Destination XRPL address (r…)"
              value={withdrawDest}
              onChange={(e) => setWithdrawDest(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm font-mono"
            />
            <Input
              data-testid="withdraw-tag-input"
              type="number"
              placeholder="Destination tag (optional — needed for exchanges)"
              value={withdrawTag}
              onChange={(e) => setWithdrawTag(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <Input
              data-testid="withdraw-note-input"
              placeholder="Note (optional)"
              value={withdrawNote}
              onChange={(e) => setWithdrawNote(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <p className="text-[10px] text-amber-400 leading-snug">
              ⚠️ Keeps ≥ 10 XRP on-chain for platform operations. Double-check the destination — XRPL transactions are irreversible.
            </p>
            <Button
              data-testid="withdraw-confirm-btn"
              onClick={handleTreasuryWithdraw}
              disabled={loading || !withdrawAmount || !withdrawDest}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-10"
            >
              {loading ? 'Sending…' : 'Send XRP'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buyback Dialog */}
      <Dialog open={showBuybackDialog} onOpenChange={setShowBuybackDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Trigger $GHOST Buyback</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Use treasury fees to buy $GHOST</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input type="number" placeholder="Amount (XRP)" value={buybackAmount} onChange={(e) => setBuybackAmount(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
            <Input placeholder="Wallet address" value={buybackWallet} onChange={(e) => setBuybackWallet(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
            <Button onClick={handleBuyback} disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 h-10">{loading ? 'Processing...' : 'Execute Buyback'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NFT Purchase Dialog */}
      <Dialog open={showNftDialog} onOpenChange={setShowNftDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Buy NFT</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Use treasury fees to purchase an NFT</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input type="number" placeholder="Amount (XRP)" value={nftAmount} onChange={(e) => setNftAmount(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
            <Input placeholder="NFT Token ID" value={nftId} onChange={(e) => setNftId(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
            <Input placeholder="Wallet address" value={nftWallet} onChange={(e) => setNftWallet(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
            <Button onClick={handleNftPurchase} disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700 h-10">{loading ? 'Processing...' : 'Execute Purchase'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GhostStat({ label, value, accent = 'text-white' }) {
  return (
    <div className="bg-slate-900/40 rounded-lg px-2 py-1.5 text-center">
      <p className={`text-sm font-bold ${accent}`}>{value}</p>
      <p className="text-[9px] text-slate-500 leading-none mt-0.5">{label}</p>
    </div>
  );
}

function MiniBarChart({ data, valueKey, color }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div className="flex items-end gap-[3px] h-16">
      {data.map((d, idx) => {
        const height = Math.max((d[valueKey] / max) * 100, 4);
        return (
          <div key={idx} className="flex-1 flex flex-col items-center gap-0.5">
            <div className={`w-full rounded-sm ${color} opacity-80 transition-all`} style={{ height: `${height}%` }}
              title={`${d.label}: ${d[valueKey]}`} />
            {idx % 3 === 0 && <span className="text-[7px] text-slate-600 leading-none">{d.label?.slice(0, 3)}</span>}
          </div>
        );
      })}
    </div>
  );
}

export default AdminPanel;
